import React from 'react';
import { Button, Icon, Text, Tooltip, theme } from 'components';
import styled from 'styled-components';
import { featureEnabled } from 'utils';
import { mlsStaffMenuOptionsMap } from 'components/SideMenu/MenuSection/types';
import { useHistory } from 'react-router-dom';
import { AuthContext } from 'contexts';
import LockOutlined from '@ant-design/icons/LockOutlined';
import SearchBar from './SearchBar';
import { useMLSStaffContext } from './MLSStaffContext';
import AddUserPopover from './MLSStaffUsersPage/AddUserPopover';

const MLSStaffRightHeader: React.FC = () => {
  const {
    selectedMenu,
    total,
    initialTotal,
    csvLink,
    loading,
    selectedLockboxes,
    selectedUsers,
    setModalVisible,
    addUserPopoverVisible,
    setAddUserPopoverVisible,
    setManualCoopModalVisible,
    setLockboxPermissionsModalVisible,
  } = useMLSStaffContext();

  const { auth } = React.useContext(AuthContext);

  const features = auth?.org.features;

  const history = useHistory();
  const handleActivityClickBtn = () => {
    if (selectedMenu === 'lockboxes') {
      const query = selectedLockboxes.reduce(
        (acc, lockbox) => `${acc}${lockbox},`,
        '',
      );
      history.push(`/mls/lockboxesActivities?lockboxIds=${query}`);
    } else if (selectedMenu === 'users') {
      const query = selectedUsers.reduce((acc, user) => `${acc}${user},`, '');
      history.push(`/mls/lockboxesActivities?userIds=${query}`);
    }
  };

  const handleQuickAssignActivityClickBtn = () => {
    history.push(`/mls/quick-assignment-activity`);
  };

  const handleLockboxPermissionsClick = () => {
    setLockboxPermissionsModalVisible(true);
  };

  const addUserElement = featureEnabled('supraManualCoopFeature') ? (
    <AddUserPopover
      visible={addUserPopoverVisible}
      setVisible={setAddUserPopoverVisible}
      setModalVisible={setModalVisible}
      setManualCoopModalVisible={setManualCoopModalVisible}
    />
  ) : (
    <Button
      onClick={() => {
        setModalVisible(true);
      }}
    >
      <Icon type="plus" offset={[0, 2]} />
      Add User
    </Button>
  );

  return (
    <StyledContainer>
      {features.includes('MLS_STAFF_ADD_USERS') &&
        !loading &&
        selectedMenu === 'users' &&
        addUserElement}
      {!loading && selectedMenu !== 'brokerages' ? (
        <Button
          disabled={!selectedLockboxes.length && !selectedUsers.length}
          onClick={handleActivityClickBtn}
        >
          View Activities
        </Button>
      ) : null}

      {!loading &&
        selectedMenu === 'lockboxes' &&
        featureEnabled('quickLockboxAssignmentFeature') &&
        features.includes('MLS_STAFF_QUICK_LOCKBOX_ASSIGNMENT') && (
          <>
            <Button onClick={handleQuickAssignActivityClickBtn}>
              View Quick Assignment Activities
            </Button>
          </>
        )}

      {!loading &&
        selectedMenu === 'lockboxes' &&
        featureEnabled('bulkLockboxPermissionsFeature', 1) && (
          <>
            <Button onClick={handleLockboxPermissionsClick}>
              <LockOutlineSvg />
              Manage Permissions
            </Button>
          </>
        )}

      {!loading && (
        <StyledText data-count={total} data-total={initialTotal}>
          {total} of {initialTotal} {mlsStaffMenuOptionsMap[selectedMenu]}
        </StyledText>
      )}

      <SearchBar type={selectedMenu} />

      <Tooltip
        title="Can only export up to 50 pages from the current page."
        placement="topRight"
        data-cy="mls-staff-tooltip"
      >
        <StyledButton
          data-cy="export-button"
          href={csvLink}
          rel="noopener noreferrer"
          target="_blank"
          disabled={loading}
        >
          <StyledIcon type="antdFileTextOutlined" width={12} color="black" />{' '}
          Export .csv
        </StyledButton>
      </Tooltip>
    </StyledContainer>
  );
};

export default MLSStaffRightHeader;

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding-bottom: 5px;
`;

const StyledText = styled(Text)`
  color: ${theme.textColorSecondary};
  font-size: 14px;
`;

const StyledIcon = styled(Icon)`
  margin-top: 1px;
`;

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 3px !important;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.85);
`;

const LockOutlineSvg: React.FC = () => (
  <svg
    width="12"
    height="14"
    viewBox="0 0 12 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ marginRight: 6, flexShrink: 0 }}
  >
    <path
      d="M5.66667 0C7.87581 0 9.66667 1.78742 9.66667 3.99232V6.014L10 6.01408C10.7364 6.01408 11.3333 6.60988 11.3333 7.34485V12.0026C11.3333 12.7375 10.7364 13.3333 10 13.3333H1.33333C0.596954 13.3333 0 12.7375 0 12.0026V7.34485C0 6.60988 0.596954 6.01408 1.33333 6.01408L1.66667 6.014V3.99232C1.66667 1.78742 3.45753 0 5.66667 0ZM10 7.01216H1.33333C1.16239 7.01216 1.0215 7.14059 1.00224 7.30605L1 7.34485V12.0026C1 12.1732 1.12868 12.3138 1.29446 12.333L1.33333 12.3353H10C10.1709 12.3353 10.3118 12.2068 10.3311 12.0414L10.3333 12.0026V7.34485C10.3333 7.17423 10.2047 7.03361 10.0389 7.01439L10 7.01216ZM6.33333 8.78336V10.5129C6.33333 10.5865 6.27364 10.6462 6.2 10.6462H5.46667C5.39303 10.6462 5.33333 10.5865 5.33333 10.5129V8.78336C5.33333 8.70973 5.39303 8.65003 5.46667 8.65003H6.2C6.27364 8.65003 6.33333 8.70973 6.33333 8.78336ZM5.66667 0.998081C4.0394 0.998081 2.71474 2.2912 2.66795 3.90404L2.66667 3.99232V5.98848H8.66667V3.99232C8.66667 2.36818 7.37106 1.04606 5.75512 0.999357L5.66667 0.998081Z"
      fill="currentColor"
    />
  </svg>
);
